/*
 * THE BUBBLES' GLIDE, DONE IN THE PAINT PASS INSTEAD OF BY THE BROWSER (2026-10-18, Chris: "There is DEFINITELY STILL SKIPPING AND CHOPPINESS... MUST be smooth all the time").
 *
 * Settings > Application > Bubble Speed eases every real-time bubble (and its icon) between the positions the simulation writes, with a CSS transition on cx / cy / x / y. A transition on
 * those properties cannot run on the GPU: the browser re-computes the style of every gliding element on EVERY display frame, and on a 120 Hz screen that is 120 times a second for each
 * of ~80 bubbles and ~80 icons. Measured in a real Chrome on this Mac (perf/ablate.mjs, the same crowd, the untouched window before and after bracketing the changed one): with the
 * transitions switched off, the main thread went from 42% busy to 32% - the single biggest item on the page. (The same crowd drawn without bubbles at all: 34%.)
 *
 * The default speed ("Fast") is 100 ms. At that speed the glide is a small smoothing of the simulation's jitter, so this does it itself, once per paint (60 a second, see
 * simulationPacer.ts), as an exponential follow: each paint moves the shown position a fixed fraction of the way to where the simulation put the bubble. A CSS ease-out transition
 * that is restarted on every new target (which is what the simulation does, every tick) behaves as exactly that, with a time constant of about 0.58 x the duration (the curve's opening
 * slope is 1 / 0.58). "Medium" (1 s) and "Slow" (5 s) are the same follow with a longer time constant: they were left to the browser's transition at first (they last long after the simulation has stopped), which the next paragraph undoes.
 *
 * EVERY SPEED, NOT ONLY FAST (2026-10-20, Chris: "Bubbles and icons stutter the most when APPROACHING their destination ... on medium it seems to be worse" and "the double circular arrows of the
 * return visitor separate from their respective bubbles"). Medium and Slow kept the browser's transition, and a real Chrome on the 120 Hz screen (perf/approach.mjs, which reads what is PAINTED)
 * showed what that costs. Every write of a new target restarts every element's transition on its own clock, and the bubble (cx, cy) and its return mark (x, y) are written on different frames
 * (their positions are snapped to different grids), so they glide on different curves: the mark ended up as far as 36 px from its bubble (99th percentile 6.4 px; the same measure at Fast: 0.7).
 * Done here, ONE shown position per bubble is computed once per paint and written to the bubble, its icon and every mark and badge in the same pass, so they cannot part, and the glide is
 * smooth at any speed. The paint pass keeps running while a glide is in flight after the simulation has cooled (simulationPacer.ts `keepPainting`, asked `moving`), so a Slow glide still
 * finishes on its own.
 *
 * Pure (no DOM), so the follow, its time constant and the bookkeeping can be tested without a browser.
 */

/** the time constant of a CSS `ease-out` transition (cubic-bezier(0, 0, .58, 1)) that is restarted every frame, as a fraction of its duration: 1 / (the curve's opening slope, 1 / .58) */
export const GLIDE_TAU_PER_MS = 0.58;
/** within this distance (px) of the target the shown position snaps onto it, so a bubble at rest is exactly where the simulation put it and the writes stop (a tenth of a device pixel at 2x) */
export const GLIDE_REST_PX = 0.05;
/** a gap longer than this between paints is treated as this long: after a pause the bubbles do not owe a long glide */
export const MAX_GLIDE_DT_MS = 250;

/** true when this speed's glide is done here (and the CSS transition must be off for it): every speed but "none" */
export const usesJsGlide = (moveMs: number): boolean => moveMs > 0;

/** the transition duration the stylesheet should be given: nothing wherever the paint pass does the glide, which is every speed */
export const cssMoveMs = (moveMs: number): number => (usesJsGlide(moveMs) ? 0 : moveMs);

/** The fraction of the remaining distance to cover in one paint that comes `dtMs` after the last, for a glide of `moveMs`. 0 for no time, tending to 1 for a long one. */
export const glideFactor = (dtMs: number, moveMs: number): number => {
  if (!(moveMs > 0)) return 1;
  const dt = Math.min(MAX_GLIDE_DT_MS, Math.max(0, dtMs));

  return 1 - Math.exp(-dt / (GLIDE_TAU_PER_MS * moveMs));
};

export interface GlidePoint {
  x: number;
  y: number;
}

interface Tracked extends GlidePoint {
  seen: number;
}

/** The shown position of every bubble, by visitor id. One pass is `begin()`, a `follow()` for each bubble drawn, then `end()`, which forgets the bubbles that were not drawn. */
export class BubbleGlide {
  private points = new Map<string, Tracked>();
  private pass = 0;
  private movingNow = false;

  begin(): void {
    this.pass++;
    this.movingNow = false;
  }

  /** true when, in the last pass, some bubble was still on its way (not yet within GLIDE_REST_PX of where the simulation put it): another paint is owed even if the simulation has stopped */
  get moving(): boolean {
    return this.movingNow;
  }

  /**
   * Move `id`'s shown position `factor` of the way to (targetX, targetY) and return it. A bubble seen for the first time starts AT its target (the browser did not glide a new element
   * either); `snap` puts it on the target at once (a silent rebuild, a bubble held by the pointer); a target that is not a number is passed straight through, as it always was.
   */
  follow(id: string, targetX: number, targetY: number, factor: number, snap = false): GlidePoint {
    let p = this.points.get(id);
    if (!p) {
      p = { x: targetX, y: targetY, seen: this.pass };
      this.points.set(id, p);

      return p;
    }
    p.seen = this.pass;
    if (snap || !Number.isFinite(targetX) || !Number.isFinite(targetY) || !Number.isFinite(p.x) || !Number.isFinite(p.y)) {
      p.x = targetX;
      p.y = targetY;

      return p;
    }
    p.x += (targetX - p.x) * factor;
    p.y += (targetY - p.y) * factor;
    if (Math.abs(targetX - p.x) < GLIDE_REST_PX) p.x = targetX;
    if (Math.abs(targetY - p.y) < GLIDE_REST_PX) p.y = targetY;
    if (p.x !== targetX || p.y !== targetY) this.movingNow = true;

    return p;
  }

  end(): void {
    this.points.forEach((p, id) => {
      if (p.seen !== this.pass) this.points.delete(id);
    });
  }

  clear(): void {
    this.points.clear();
    this.movingNow = false;
  }

  get size(): number {
    return this.points.size;
  }
}
